import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/common/Layout';
import { userService, CreateUserData, UpdateUserData, UserFilters } from '../services/userService';
import { User } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import { useAppData } from '../context/AppDataContext';
import { FiSearch, FiX } from 'react-icons/fi';
import Icon from '../components/common/Icon';

const Users: React.FC = () => {
  const { hasPermission } = usePermissions();
  const { refreshUsers, brands } = useAppData();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [assignedBrandIds, setAssignedBrandIds] = useState<number[]>([]);
  const [brandsDropdownOpen, setBrandsDropdownOpen] = useState(false);
  const brandsDropdownRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState<CreateUserData>({
    name: '',
    phone: '',
    email: '',
    password: '',
    role: 'executive',
    status: 'active'
  });
  const [filters, setFilters] = useState<UserFilters>({});
  const [searchTerm, setSearchTerm] = useState('');
  const hasLoadedRef = useRef(false);
  const filtersInitialized = useRef(false);

  // Always fetch fresh users list on page visit (to see other users' updates)
  useEffect(() => {
    // Check permission before loading
    if (!hasPermission('users', 'view')) {
      setError('You do not have permission to view users');
      filtersInitialized.current = true; // Prevent filters from trying to load
      return;
    }

    // Prevent duplicate calls from React StrictMode
    if (hasLoadedRef.current) {
      return;
    }
    hasLoadedRef.current = true;
    
    const loadData = async () => {
      await loadUsers();
      // Set after load completes to prevent filters useEffect from running on initial mount
      filtersInitialized.current = true;
    };
    loadData();
  }, []);

  // Reload users when filters change (but not on initial mount)
  useEffect(() => {
    // Only run if filters have been initialized (after initial load completes)
    if (filtersInitialized.current) {
      loadUsers();
    }
  }, [filters]);

  // Close brands dropdown when clicking outside
  useEffect(() => {
    if (!brandsDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (brandsDropdownRef.current && !brandsDropdownRef.current.contains(e.target as Node)) {
        setBrandsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [brandsDropdownOpen]);

  const loadUsers = async () => {
    // Check permission before making API call
    if (!hasPermission('users', 'view')) {
      setError('You do not have permission to view users');
      setUsers([]);
      return;
    }

    // Prevent duplicate concurrent calls
    if (loading) {
      return;
    }
    try {
      setLoading(true);
      const data = await userService.getAll(filters);
      setUsers(data);
      setError('');
    } catch (err: any) {
      // Handle 403 Forbidden (permission denied) gracefully
      if (err.response?.status === 403) {
        setError('You do not have permission to view users');
        setUsers([]);
      } else {
        setError(err.response?.data?.message || 'Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setFilters({
      ...filters,
      search: searchTerm || undefined
    });
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let userId: number | null = null;
      if (editingUser) {
        const updateData: UpdateUserData = { ...formData };
        if (!updateData.password) {
          delete updateData.password;
        }
        await userService.update(editingUser.id, updateData);
        userId = editingUser.id;
      } else {
        const created = await userService.create(formData);
        userId = created.id;
      }
      if (formData.role === 'manager' && userId) {
        await userService.setAssignedBrands(userId, assignedBrandIds);
      }
      setShowModal(false);
      setEditingUser(null);
      setAssignedBrandIds([]);
      setFormData({ name: '', phone: '', email: '', password: '', role: 'executive', status: 'active' });
      // Refresh users list and context (so other pages have latest data)
      await Promise.all([loadUsers(), refreshUsers()]);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save user');
    }
  };

  const handleEdit = async (user: User) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      phone: user.phone,
      email: user.email || '',
      password: '',
      role: user.role,
      status: user.status
    });
    if (user.role === 'manager') {
      try {
        const ids = await userService.getAssignedBrands(user.id);
        setAssignedBrandIds(ids);
      } catch {
        setAssignedBrandIds([]);
      }
    } else {
      setAssignedBrandIds([]);
    }
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }
    try {
      await userService.delete(id);
      // Refresh users list and context (so other pages have latest data)
      await Promise.all([loadUsers(), refreshUsers()]);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'org_admin':
        return 'bg-yellow-100 text-yellow-800';
      case 'manager':
        return 'bg-blue-100 text-blue-800';
      case 'executive':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Layout>
      <div className="space-y-3 sm:space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">User Management</h1>
          <button
            onClick={() => {
              setEditingUser(null);
              setAssignedBrandIds([]);
              setFormData({ name: '', phone: '', email: '', password: '', role: 'executive', status: 'active' });
              setShowModal(true);
            }}
            className="bg-yellow-500 text-black-950 px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-yellow-600 text-sm sm:text-base font-medium w-full sm:w-auto font-bold"
          >
            Add User
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
            <div>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search name or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 pl-8 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
                <Icon component={FiSearch} className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
            </div>
            <div>
              <select
                value={filters.role || ''}
                onChange={(e) => setFilters({ ...filters, role: e.target.value as any || undefined })}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <option value="">All Roles</option>
                <option value="org_admin">Org Admin</option>
                <option value="manager">Manager</option>
                <option value="executive">Executive</option>
              </select>
            </div>
            <div>
              <select
                value={filters.status || ''}
                onChange={(e) => setFilters({ ...filters, status: e.target.value as any || undefined })}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSearch}
                className="flex-1 bg-yellow-500 text-black-950 px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-yellow-600 text-sm font-medium flex items-center justify-center gap-2 font-bold"
              >
                <Icon component={FiSearch} className="w-4 h-4" />
                <span>Search</span>
              </button>
              {(filters.search || filters.role || filters.status) && (
                <button
                  onClick={handleClearFilters}
                  className="bg-gray-200 text-gray-700 px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-gray-300 text-sm font-medium flex items-center justify-center gap-2"
                >
                  <Icon component={FiX} className="w-4 h-4" />
                  <span>Clear</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-6 text-sm text-gray-600">Loading...</div>
        ) : (
          <div className="bg-white shadow-sm overflow-hidden rounded-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 sm:px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap text-xs sm:text-sm font-medium text-gray-900">
                        {user.name}
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap text-xs sm:text-sm text-gray-500">
                        {user.phone}
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap">
                        <span className={`px-1.5 sm:px-2 inline-flex text-xs leading-4 sm:leading-5 font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                          {user.role.replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap">
                        <span className={`px-1.5 sm:px-2 inline-flex text-xs leading-4 sm:leading-5 font-semibold rounded-full ${
                          user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {user.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap text-xs sm:text-sm font-medium">
                        {hasPermission('users', 'edit') && (
                          <button
                            onClick={() => handleEdit(user)}
                            className="text-yellow-600 hover:text-yellow-700 mr-2 sm:mr-4"
                          >
                            Edit
                          </button>
                        )}
                        {hasPermission('users', 'delete') && (
                          <button
                            onClick={() => handleDelete(user.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {users.length === 0 && !loading && (
              <div className="text-center py-8 text-sm text-gray-500">No users found</div>
            )}
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-3 sm:p-4">
            <div className="relative top-2 sm:top-10 mx-auto p-4 sm:p-5 border w-full max-w-2xl shadow-xl rounded-lg bg-white">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">
                  {editingUser ? 'Edit User' : 'Add User'}
                </h3>
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingUser(null); setAssignedBrandIds([]); setBrandsDropdownOpen(false); }}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  aria-label="Close"
                >
                  <Icon component={FiX} className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Password {editingUser && <span className="text-gray-500 text-xs">(leave blank to keep current)</span>}
                    </label>
                    <input
                      type="password"
                      required={!editingUser}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-gray-500 text-xs">(optional)</span>
                    </label>
                    <input
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                      placeholder="user@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Role</label>
                    <select
                      value={formData.role}
                      onChange={(e) => {
                        const role = e.target.value as any;
                        setFormData({ ...formData, role });
                        if (role !== 'manager') setAssignedBrandIds([]);
                      }}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    >
                      <option value="executive">Executive</option>
                      <option value="manager">Manager</option>
                      <option value="org_admin">Org Admin</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                      className="block w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
                {formData.role === 'manager' && (
                  <div ref={brandsDropdownRef} className="relative">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Assigned brands <span className="text-gray-500 text-xs">(manager can only see leads & customers for these brands)</span>
                    </label>
                    <div
                      role="combobox"
                      aria-expanded={brandsDropdownOpen}
                      aria-haspopup="listbox"
                      tabIndex={0}
                      onClick={() => setBrandsDropdownOpen((v) => !v)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') setBrandsDropdownOpen(false);
                      }}
                      className="min-h-[38px] w-full border border-gray-300 rounded-md bg-white px-2 py-1.5 flex flex-wrap items-center gap-1.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    >
                      {assignedBrandIds.length === 0 && !brandsDropdownOpen && (
                        <span className="text-gray-400 text-sm">Click to select brands</span>
                      )}
                      {assignedBrandIds.map((id) => {
                        const brand = brands.find((b) => b.id === id);
                        return (
                          <span
                            key={id}
                            className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded bg-yellow-100 text-yellow-900 text-xs font-medium border border-yellow-300"
                          >
                            {brand?.name ?? `#${id}`}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setAssignedBrandIds((prev) => prev.filter((x) => x !== id));
                              }}
                              className="rounded p-0.5 hover:bg-yellow-200 focus:outline-none focus:ring-1 focus:ring-yellow-500"
                              aria-label={`Remove ${brand?.name ?? id}`}
                            >
                              <Icon component={FiX} className="w-3 h-3" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                    {brandsDropdownOpen && (
                      <div className="absolute z-10 mt-1 w-full border border-gray-300 rounded-md bg-white shadow-lg max-h-48 overflow-y-auto">
                        <ul role="listbox" className="py-1">
                          {brands.length === 0 ? (
                            <li className="px-3 py-2 text-sm text-gray-500">No brands available.</li>
                          ) : (
                            brands.map((b) => {
                              const isSelected = assignedBrandIds.includes(b.id);
                              return (
                                <li
                                  key={b.id}
                                  role="option"
                                  aria-selected={isSelected}
                                  onClick={() => {
                                    setAssignedBrandIds((prev) =>
                                      isSelected ? prev.filter((x) => x !== b.id) : [...prev, b.id]
                                    );
                                  }}
                                  className={`px-3 py-2 text-sm cursor-pointer flex items-center justify-between ${isSelected ? 'bg-yellow-50 text-yellow-900' : 'hover:bg-gray-50'}`}
                                >
                                  <span>{b.name}</span>
                                  {isSelected && <span className="text-yellow-600 text-xs">✓</span>}
                                </li>
                              );
                            })
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-2 border-t border-gray-200 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingUser(null);
                      setAssignedBrandIds([]);
                      setBrandsDropdownOpen(false);
                    }}
                    className="px-3 py-1.5 sm:px-4 sm:py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1.5 sm:px-4 sm:py-2 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold"
                  >
                    {editingUser ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Users;

